import { defineMessages, type MessageDescriptor } from 'react-intl';

export type EventTypeMessages = {
	absolute: MessageDescriptor;
	relative: MessageDescriptor;
	standalone: MessageDescriptor;
};

// =============================================================================
// Legacy messages (used when qf_icu_select_event_type_messages is OFF)
// =============================================================================

export const clickedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage: '{collaborator} clicked on {absoluteTime}',
		description: 'action taken by the user on this object with absolute timestamp - clicked',
		id: 'recommended.eventTypeMessage.absolute.clicked',
	},
	relative: {
		defaultMessage: '{collaborator} clicked {relativeTime}',
		description: 'action taken by the user on this object with relative timestamp - clicked',
		id: 'recommended.eventTypeMessage.relative.clicked',
	},
	standalone: {
		defaultMessage: '{collaborator} clicked',
		description: 'action taken by the user on this object - clicked',
		id: 'recommended.eventTypeMessage.clicked',
	},
});

export const commentedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage: '{collaborator} commented on {absoluteTime}',
		description: 'action taken on this object with absolute timestamp - comment added',
		id: 'recommended.eventTypeMessage.absolute.commented',
	},
	relative: {
		defaultMessage: '{collaborator} commented {relativeTime}',
		description: 'action taken by the user on this object with relative timestamp - comment added',
		id: 'recommended.eventTypeMessage.relative.commented',
	},
	standalone: {
		defaultMessage: '{collaborator} commented',
		description: 'action taken by the user on this object - comment added',
		id: 'recommended.eventTypeMessage.commented',
	},
});

export const editedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage: '{collaborator} edited on {absoluteTime}',
		description: 'action taken by the user on this object with absolute timestamp - edited',
		id: 'recommended.eventTypeMessage.absolute.edited',
	},
	relative: {
		defaultMessage: '{collaborator} edited {relativeTime}',
		description: 'action taken by the user on this object with relative timestamp - edited',
		id: 'recommended.eventTypeMessage.relative.edited',
	},
	standalone: {
		defaultMessage: '{collaborator} edited',
		description: 'action taken by the user on this object - edited',
		id: 'recommended.eventTypeMessage.edited',
	},
});

export const viewedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage: '{collaborator} viewed on {absoluteTime}',
		description: 'action taken by the user on this object with absolute timestamp - viewed',
		id: 'recommended.eventTypeMessage.absolute.viewed',
	},
	relative: {
		defaultMessage: '{collaborator} viewed {relativeTime}',
		description: 'action taken by the user on this object with relative timestamp - viewed',
		id: 'recommended.eventTypeMessage.relative.viewed',
	},
	standalone: {
		defaultMessage: '{collaborator} viewed',
		description: 'action taken by the user on this object - viewed',
		id: 'recommended.eventTypeMessage.viewed',
	},
});

export const mentionedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage: '{collaborator} were mentioned on {absoluteTime}',
		description: 'action taken by the user on this object with absolute timestamp - were mentioned',
		id: 'recommended.eventTypeMessage.absolute.wereMentioned',
	},
	relative: {
		defaultMessage: '{collaborator} were mentioned {relativeTime}',
		description: 'action taken by the user on this object with relative timestamp - were mentioned',
		id: 'recommended.eventTypeMessage.relative.wereMentioned',
	},
	standalone: {
		defaultMessage: '{collaborator} were mentioned',
		description: 'action taken by the user on this object - were mentioned',
		id: 'recommended.eventTypeMessage.wereMentioned',
	},
});

export const sharedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage: '{collaborator} shared on {absoluteTime}',
		description: 'action taken by the user on this object with absolute timestamp - shared',
		id: 'recommended.eventTypeMessage.absolute.shared',
	},
	relative: {
		defaultMessage: '{collaborator} shared {relativeTime}',
		description: 'action taken by the user on this object with relative timestamp - shared',
		id: 'recommended.eventTypeMessage.relative.shared',
	},
	standalone: {
		defaultMessage: '{collaborator} shared',
		description: 'action taken by the user on this object - shared',
		id: 'recommended.eventTypeMessage.shared',
	},
});

export const reactedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage: '{collaborator} reacted on {absoluteTime}',
		description: 'action taken by the user on this object with absolute timestamp - reacted',
		id: 'recommended.eventTypeMessage.absolute.reacted',
	},
	relative: {
		defaultMessage: '{collaborator} reacted {relativeTime}',
		description: 'action taken by the user on this object with relative timestamp - reacted',
		id: 'recommended.eventTypeMessage.relative.reacted',
	},
	standalone: {
		defaultMessage: '{collaborator} reacted',
		description: 'action taken by the user on this object - reacted',
		id: 'recommended.eventTypeMessage.reacted',
	},
});

export const readEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage: '{collaborator} read on {absoluteTime}',
		description: 'action taken by the user on this object with absolute timestamp - read',
		id: 'recommended.eventTypeMessage.absolute.read',
	},
	relative: {
		defaultMessage: '{collaborator} read {relativeTime}',
		description: 'action taken by the user on this object with relative timestamp - read',
		id: 'recommended.eventTypeMessage.relative.read',
	},
	standalone: {
		defaultMessage: '{collaborator} read',
		description: 'action taken by the user on this object - read',
		id: 'recommended.eventTypeMessage.read',
	},
});

export const createdEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage: '{collaborator} created on {absoluteTime}',
		description: 'action taken by the user on this object with absolute timestamp - created',
		id: 'recommended.eventTypeMessage.absolute.created',
	},
	relative: {
		defaultMessage: '{collaborator} created {relativeTime}',
		description: 'action taken by the user on this object with relative timestamp - created',
		id: 'recommended.eventTypeMessage.relative.created',
	},
	standalone: {
		defaultMessage: '{collaborator} created',
		description: 'action taken by the user on this object - created',
		id: 'recommended.eventTypeMessage.created',
	},
});

export const interactedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage: '{collaborator} interacted on {absoluteTime}',
		description: 'action taken by the user on this object with absolute timestamp - interacted',
		id: 'recommended.eventTypeMessage.absolute.interacted',
	},
	relative: {
		defaultMessage: '{collaborator} interacted {relativeTime}',
		description: 'action taken by the user on this object with relative timestamp - interacted',
		id: 'recommended.eventTypeMessage.relative.interacted',
	},
	standalone: {
		defaultMessage: '{collaborator} interacted',
		description: 'action taken by the user on this object - interacted',
		id: 'recommended.eventTypeMessage.interacted',
	},
});

// =============================================================================
// Legacy messages (used when qf_icu_select_event_type_messages is OFF)
// =============================================================================

export const messages: {
	you: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	collaborator: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	you: {
		id: 'recommended.you',
		defaultMessage: 'You',
		description:
			'Label to indicate the user themselves in event messages, e.g. "You commented on this page."',
	},
	collaborator: {
		id: 'recommended.collaborator',
		defaultMessage: 'Collaborator',
		description:
			'Label to indicate a collaborator in event messages, e.g. "Collaborator commented on this page."',
	},
});

// =============================================================================
// ICU select messages (used when qf_icu_select_event_type_messages is ON)
// Uses ICU MessageFormat `select` so translators receive the full sentence and
// verb conjugation can vary correctly per subject in other languages.
// `actor` is 'you' | 'collaborator'; `username` is the `other` branch for real
// user names in a future iteration.
// =============================================================================

export const icuClickedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage:
			'{actor, select, you {You clicked on {absoluteTime}} collaborator {Collaborator clicked on {absoluteTime}} other {{username} clicked on {absoluteTime}}}',
		description:
			'Action taken by the user on this object with absolute timestamp - clicked. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject, verb and time freely.',
		id: 'recommended.eventTypeMessage.icu.absolute.clicked',
	},
	relative: {
		defaultMessage:
			'{actor, select, you {You clicked {relativeTime}} collaborator {Collaborator clicked {relativeTime}} other {{username} clicked {relativeTime}}}',
		description:
			'Action taken by the user on this object with relative timestamp - clicked. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject, verb and time freely.',
		id: 'recommended.eventTypeMessage.icu.relative.clicked',
	},
	standalone: {
		defaultMessage:
			'{actor, select, you {You clicked} collaborator {Collaborator clicked} other {{username} clicked}}',
		description:
			'Action taken by the user on this object - clicked. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject and verb freely.',
		id: 'recommended.eventTypeMessage.icu.clicked',
	},
});

export const icuCommentedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage:
			'{actor, select, you {You commented on {absoluteTime}} collaborator {Collaborator commented on {absoluteTime}} other {{username} commented on {absoluteTime}}}',
		description:
			'Action taken on this object with absolute timestamp - comment added. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject, verb and time freely.',
		id: 'recommended.eventTypeMessage.icu.absolute.commented',
	},
	relative: {
		defaultMessage:
			'{actor, select, you {You commented {relativeTime}} collaborator {Collaborator commented {relativeTime}} other {{username} commented {relativeTime}}}',
		description:
			'Action taken by the user on this object with relative timestamp - comment added. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject, verb and time freely.',
		id: 'recommended.eventTypeMessage.icu.relative.commented',
	},
	standalone: {
		defaultMessage:
			'{actor, select, you {You commented} collaborator {Collaborator commented} other {{username} commented}}',
		description:
			'Action taken by the user on this object - comment added. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject and verb freely.',
		id: 'recommended.eventTypeMessage.icu.commented',
	},
});

export const icuEditedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage:
			'{actor, select, you {You edited on {absoluteTime}} collaborator {Collaborator edited on {absoluteTime}} other {{username} edited on {absoluteTime}}}',
		description:
			'Action taken by the user on this object with absolute timestamp - edited. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject, verb and time freely.',
		id: 'recommended.eventTypeMessage.icu.absolute.edited',
	},
	relative: {
		defaultMessage:
			'{actor, select, you {You edited {relativeTime}} collaborator {Collaborator edited {relativeTime}} other {{username} edited {relativeTime}}}',
		description:
			'Action taken by the user on this object with relative timestamp - edited. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject, verb and time freely.',
		id: 'recommended.eventTypeMessage.icu.relative.edited',
	},
	standalone: {
		defaultMessage:
			'{actor, select, you {You edited} collaborator {Collaborator edited} other {{username} edited}}',
		description:
			'Action taken by the user on this object - edited. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject and verb freely.',
		id: 'recommended.eventTypeMessage.icu.edited',
	},
});

export const icuViewedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage:
			'{actor, select, you {You viewed on {absoluteTime}} collaborator {Collaborator viewed on {absoluteTime}} other {{username} viewed on {absoluteTime}}}',
		description:
			'Action taken by the user on this object with absolute timestamp - viewed. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject, verb and time freely.',
		id: 'recommended.eventTypeMessage.icu.absolute.viewed',
	},
	relative: {
		defaultMessage:
			'{actor, select, you {You viewed {relativeTime}} collaborator {Collaborator viewed {relativeTime}} other {{username} viewed {relativeTime}}}',
		description:
			'Action taken by the user on this object with relative timestamp - viewed. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject, verb and time freely.',
		id: 'recommended.eventTypeMessage.icu.relative.viewed',
	},
	standalone: {
		defaultMessage:
			'{actor, select, you {You viewed} collaborator {Collaborator viewed} other {{username} viewed}}',
		description:
			'Action taken by the user on this object - viewed. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject and verb freely.',
		id: 'recommended.eventTypeMessage.icu.viewed',
	},
});

export const icuMentionedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage:
			'{actor, select, you {You were mentioned on {absoluteTime}} collaborator {Collaborator was mentioned on {absoluteTime}} other {{username} was mentioned on {absoluteTime}}}',
		description:
			'Action taken by the user on this object with absolute timestamp - were/was mentioned. `actor` can be "you" (were), "collaborator" (was), or anything else (uses `username`, was).',
		id: 'recommended.eventTypeMessage.icu.absolute.mentioned',
	},
	relative: {
		defaultMessage:
			'{actor, select, you {You were mentioned {relativeTime}} collaborator {Collaborator was mentioned {relativeTime}} other {{username} was mentioned {relativeTime}}}',
		description:
			'Action taken by the user on this object with relative timestamp - were/was mentioned. `actor` can be "you" (were), "collaborator" (was), or anything else (uses `username`, was).',
		id: 'recommended.eventTypeMessage.icu.relative.mentioned',
	},
	standalone: {
		defaultMessage:
			'{actor, select, you {You were mentioned} collaborator {Collaborator was mentioned} other {{username} was mentioned}}',
		description:
			'Action taken by the user on this object - were/was mentioned. `actor` can be "you" (were), "collaborator" (was), or anything else (uses `username`, was).',
		id: 'recommended.eventTypeMessage.icu.mentioned',
	},
});

export const icuSharedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage:
			'{actor, select, you {You shared on {absoluteTime}} collaborator {Collaborator shared on {absoluteTime}} other {{username} shared on {absoluteTime}}}',
		description:
			'Action taken by the user on this object with absolute timestamp - shared. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject, verb and time freely.',
		id: 'recommended.eventTypeMessage.icu.absolute.shared',
	},
	relative: {
		defaultMessage:
			'{actor, select, you {You shared {relativeTime}} collaborator {Collaborator shared {relativeTime}} other {{username} shared {relativeTime}}}',
		description:
			'Action taken by the user on this object with relative timestamp - shared. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject, verb and time freely.',
		id: 'recommended.eventTypeMessage.icu.relative.shared',
	},
	standalone: {
		defaultMessage:
			'{actor, select, you {You shared} collaborator {Collaborator shared} other {{username} shared}}',
		description:
			'Action taken by the user on this object - shared. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject and verb freely.',
		id: 'recommended.eventTypeMessage.icu.shared',
	},
});

export const icuReactedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage:
			'{actor, select, you {You reacted on {absoluteTime}} collaborator {Collaborator reacted on {absoluteTime}} other {{username} reacted on {absoluteTime}}}',
		description:
			'Action taken by the user on this object with absolute timestamp - reacted. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject, verb and time freely.',
		id: 'recommended.eventTypeMessage.icu.absolute.reacted',
	},
	relative: {
		defaultMessage:
			'{actor, select, you {You reacted {relativeTime}} collaborator {Collaborator reacted {relativeTime}} other {{username} reacted {relativeTime}}}',
		description:
			'Action taken by the user on this object with relative timestamp - reacted. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject, verb and time freely.',
		id: 'recommended.eventTypeMessage.icu.relative.reacted',
	},
	standalone: {
		defaultMessage:
			'{actor, select, you {You reacted} collaborator {Collaborator reacted} other {{username} reacted}}',
		description:
			'Action taken by the user on this object - reacted. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject and verb freely.',
		id: 'recommended.eventTypeMessage.icu.reacted',
	},
});

export const icuReadEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage:
			'{actor, select, you {You read on {absoluteTime}} collaborator {Collaborator read on {absoluteTime}} other {{username} read on {absoluteTime}}}',
		description:
			'Action taken by the user on this object with absolute timestamp - read. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject, verb and time freely.',
		id: 'recommended.eventTypeMessage.icu.absolute.read',
	},
	relative: {
		defaultMessage:
			'{actor, select, you {You read {relativeTime}} collaborator {Collaborator read {relativeTime}} other {{username} read {relativeTime}}}',
		description:
			'Action taken by the user on this object with relative timestamp - read. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject, verb and time freely.',
		id: 'recommended.eventTypeMessage.icu.relative.read',
	},
	standalone: {
		defaultMessage:
			'{actor, select, you {You read} collaborator {Collaborator read} other {{username} read}}',
		description:
			'Action taken by the user on this object - read. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject and verb freely.',
		id: 'recommended.eventTypeMessage.icu.read',
	},
});

export const icuCreatedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage:
			'{actor, select, you {You created on {absoluteTime}} collaborator {Collaborator created on {absoluteTime}} other {{username} created on {absoluteTime}}}',
		description:
			'Action taken by the user on this object with absolute timestamp - created. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject, verb and time freely.',
		id: 'recommended.eventTypeMessage.icu.absolute.created',
	},
	relative: {
		defaultMessage:
			'{actor, select, you {You created {relativeTime}} collaborator {Collaborator created {relativeTime}} other {{username} created {relativeTime}}}',
		description:
			'Action taken by the user on this object with relative timestamp - created. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject, verb and time freely.',
		id: 'recommended.eventTypeMessage.icu.relative.created',
	},
	standalone: {
		defaultMessage:
			'{actor, select, you {You created} collaborator {Collaborator created} other {{username} created}}',
		description:
			'Action taken by the user on this object - created. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject and verb freely.',
		id: 'recommended.eventTypeMessage.icu.created',
	},
});

export const icuInteractedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage:
			'{actor, select, you {You interacted on {absoluteTime}} collaborator {Collaborator interacted on {absoluteTime}} other {{username} interacted on {absoluteTime}}}',
		description:
			'Action taken by the user on this object with absolute timestamp - interacted. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject, verb and time freely.',
		id: 'recommended.eventTypeMessage.icu.absolute.interacted',
	},
	relative: {
		defaultMessage:
			'{actor, select, you {You interacted {relativeTime}} collaborator {Collaborator interacted {relativeTime}} other {{username} interacted {relativeTime}}}',
		description:
			'Action taken by the user on this object with relative timestamp - interacted. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject, verb and time freely.',
		id: 'recommended.eventTypeMessage.icu.relative.interacted',
	},
	standalone: {
		defaultMessage:
			'{actor, select, you {You interacted} collaborator {Collaborator interacted} other {{username} interacted}}',
		description:
			'Action taken by the user on this object - interacted. `actor` can be "you", "collaborator", or anything else (uses `username`). Full sentence is inside each branch so translators can reorder subject and verb freely.',
		id: 'recommended.eventTypeMessage.icu.interacted',
	},
});
